import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Form,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined, TrophyOutlined } from '@ant-design/icons';
// Api
import {
  GET_TICKET_EVENT_MATCHUPS,
  GET_TICKET_EVENT_VOTE_RESULTS,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketEventMatchups,
  GetTicketEventMatchupsVariables,
} from 'api/ticketing/types/GetTicketEventMatchups';
import {
  GetTicketEventVoteResults,
  GetTicketEventVoteResultsVariables,
} from 'api/ticketing/types/GetTicketEventVoteResults';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketMatchupFields } from 'api/ticketing/types/TicketMatchupFields';
import { MatchupCorner, TicketEventStatus } from 'api/graphql-global-types';
// Hooks
import { useMatchcardActions } from './hooks/useMatchcardActions';
// Helpers
import { pluralize, validateForm } from 'pages/Events/helpers';
import {
  BoutFormValues,
  getCompetitorName,
  getMatchcardError,
  getVoteBreakdown,
  mapMatchupsToBouts,
} from 'pages/Events/matchcard';
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import { errorNotification } from 'ui/Notification';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import MatchcardFields from 'pages/Events/components/MatchcardFields/MatchcardFields';
// Styles
import styles from './MatchcardTab.module.scss';

const { Text, Paragraph } = Typography;

type Matchup = TicketMatchupFields;

const NO_WINNER = 'none';

const competitorIn = (matchup: Matchup, corner: MatchupCorner) =>
  matchup.competitors?.find((competitor) => competitor.corner === corner);

type MatchcardTabProps = {
  event: TicketEventDetailsFields;
  onChanged: () => void;
};

const MatchcardTab = ({ event, onChanged }: MatchcardTabProps): JSX.Element => {
  const [form] = Form.useForm<{ bouts: BoutFormValues[] }>();
  const [editing, setEditing] = useState(false);
  const isStandalone = !event.streamId;
  const cancelled = event.status === TicketEventStatus.Cancelled;

  const { data, loading, error, refetch } = useQuery<
    GetTicketEventMatchups,
    GetTicketEventMatchupsVariables
  >(GET_TICKET_EVENT_MATCHUPS, {
    variables: { input: { ticketEventId: event.id } },
    skip: !isStandalone,
    fetchPolicy: 'cache-and-network',
  });
  const matchups = data?.hostedTicketEvent.matchups || [];
  // Saving replaces the whole card, so editing waits for the saved one
  const cardLoaded = Boolean(data) && !error;
  const pollingOn = Boolean(data?.hostedTicketEvent.fanPollingEnabled);

  const { data: votesData } = useQuery<
    GetTicketEventVoteResults,
    GetTicketEventVoteResultsVariables
  >(GET_TICKET_EVENT_VOTE_RESULTS, {
    variables: { ticketEventId: event.id },
    skip: !pollingOn || !matchups.length,
    fetchPolicy: 'cache-and-network',
  });
  const { saveMatchcard, declareWinner, saving } = useMatchcardActions(
    event.id
  );

  const openEditor = (): void => {
    form.setFieldsValue({ bouts: mapMatchupsToBouts(matchups) });
    setEditing(true);
  };

  const handleSave = async (): Promise<void> => {
    const values = await validateForm(form);

    if (!values) {
      return;
    }

    const { bouts } = values;
    const cardError = getMatchcardError(bouts);

    if (cardError) {
      errorNotification(cardError);
      return;
    }

    if (await saveMatchcard(bouts)) {
      setEditing(false);
      refetch();
      onChanged();
    }
  };

  const handleWinner = (matchup: Matchup, value: string): Promise<boolean> =>
    declareWinner(
      matchup.id,
      value === NO_WINNER ? null : (value as MatchupCorner)
    );

  if (!isStandalone) {
    return (
      <Alert
        type="info"
        showIcon
        message="This event shows its stream's fight card. Edit the card and declare winners on the stream."
      />
    );
  }

  let boutNumber = 0;
  const labels = new Map(
    [...matchups]
      .sort((a, b) => a.order - b.order)
      .map((matchup) => [
        matchup.id,
        matchup.isMainEvent ? 'Main event' : `Bout ${(boutNumber += 1)}`,
      ])
  );

  return (
    <>
      <Paragraph type="secondary">
        The fight card shown on the event page. Registered fighters join the
        event as participants; invited fighters get a link to claim their spot.
        {pollingOn
          ? ' Fans can vote on each bout before the event starts.'
          : ' Turn on fan polling in Edit to let fans vote.'}
      </Paragraph>
      <PageToolbar>
        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={cancelled || !cardLoaded}
          onClick={openEditor}
        >
          {matchups.length ? 'Edit matchcard' : 'Add matchcard'}
        </Button>
      </PageToolbar>

      {error ? (
        <LoadErrorAlert
          message="Failed to load the matchcard"
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Table<Matchup>
          rowKey="id"
          loading={loading && !data}
          dataSource={[...matchups].sort((a, b) => a.order - b.order)}
          pagination={false}
          scroll={{ x: 860 }}
          locale={{ emptyText: 'No bouts on this event yet.' }}
          columns={[
            {
              title: 'Bout',
              key: 'bout',
              render: (_, matchup) =>
                matchup.isMainEvent ? (
                  <Tag color="gold">Main event</Tag>
                ) : (
                  labels.get(matchup.id)
                ),
            },
            {
              title: 'Fight',
              key: 'fight',
              render: (_, matchup) => {
                const name = (corner: MatchupCorner) => (
                  <Text
                    strong={matchup.winnerCorner === corner}
                    type={
                      matchup.winnerCorner && matchup.winnerCorner !== corner
                        ? 'secondary'
                        : undefined
                    }
                  >
                    {getCompetitorName(competitorIn(matchup, corner))}
                    {matchup.winnerCorner === corner && (
                      <TrophyOutlined className={styles.trophy} />
                    )}
                  </Text>
                );

                return (
                  <span>
                    {name(MatchupCorner.A)} <Text type="secondary">vs</Text>{' '}
                    {name(MatchupCorner.B)}
                  </span>
                );
              },
            },
            {
              title: 'Details',
              key: 'details',
              render: (_, matchup) =>
                [
                  matchup.category,
                  matchup.rounds &&
                    `${pluralize(matchup.rounds, 'round')}${
                      matchup.roundMinutes
                        ? ` × ${matchup.roundMinutes} min`
                        : ''
                    }`,
                ]
                  .filter(Boolean)
                  .join(' · ') || '—',
            },
            {
              title: 'Fan votes',
              key: 'votes',
              render: (_, matchup) => {
                const breakdown = getVoteBreakdown(
                  matchup,
                  votesData?.getTicketEventVoteResults
                );

                return breakdown ? (
                  <span>
                    {breakdown.percentA}% – {breakdown.percentB}%{' '}
                    <Text type="secondary">
                      ({pluralize(breakdown.total, 'vote')})
                    </Text>
                  </span>
                ) : (
                  <Text type="secondary">
                    {pollingOn ? 'No votes yet' : 'Off'}
                  </Text>
                );
              },
            },
            {
              title: 'Winner',
              key: 'winner',
              render: (_, matchup) => (
                <Select
                  size="small"
                  value={matchup.winnerCorner || NO_WINNER}
                  disabled={cancelled}
                  onChange={(value: string) => handleWinner(matchup, value)}
                  aria-label={`Winner of ${labels.get(matchup.id)}`}
                  className={styles.winner}
                  options={[
                    { value: NO_WINNER, label: 'Not decided' },
                    {
                      value: MatchupCorner.A,
                      label: getCompetitorName(
                        competitorIn(matchup, MatchupCorner.A)
                      ),
                    },
                    {
                      value: MatchupCorner.B,
                      label: getCompetitorName(
                        competitorIn(matchup, MatchupCorner.B)
                      ),
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      )}

      <Modal
        title="Matchcard"
        visible={editing}
        onCancel={() => setEditing(false)}
        onOk={handleSave}
        okText="Save matchcard"
        confirmLoading={saving}
        width={880}
        destroyOnClose
      >
        <Paragraph type="secondary">
          Saving replaces the whole card; bouts you delete are removed. Bouts
          you keep keep their fan votes.
        </Paragraph>
        <Form form={form} layout="vertical">
          <MatchcardFields form={form} />
        </Form>
      </Modal>
    </>
  );
};

export default MatchcardTab;
